import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Grid,
  Alert,
  Box,
  IconButton,
  Typography,
  CircularProgress
} from '@mui/material';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { ChangePasswordRequest, User, UserRole } from '../../models/User';
import { useAuth } from '../../hooks/useAuth';

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
  onSubmit: (userId: number, passwordData: ChangePasswordRequest) => Promise<void>;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  onClose,
  user,
  onSubmit
}) => {
  const { userRole, user: currentUser } = useAuth();

  const [formData, setFormData] = useState<ChangePasswordRequest>({
    currentPassword: '',
    newPassword: '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({ currentPassword: '', newPassword: '' });
      setConfirmPassword('');
      setSubmitError('');
    }
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'confirmPassword') {
      setConfirmPassword(value);
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSubmitError('');
    if (formData.newPassword !== confirmPassword) {
      setSubmitError('Yeni şifreler eşleşmiyor');
      return;
    }
    if (formData.newPassword.length < 6) {
      setSubmitError('Şifre en az 6 karakter olmalıdır');
      return;
    }
    try {
      setSubmitting(true);
      await onSubmit(user.id, formData);
      onClose();
    } catch (err) {
      console.error('Error changing password:', err);
      const error = err as any;
      if (typeof error.response?.data === 'string') {
        setSubmitError(error.response.data);
      } else if (error.response?.data?.title) {
        setSubmitError(error.response.data.title);
      } else if (error.message) {
        setSubmitError(error.message);
      } else {
        setSubmitError('Şifre değiştirilemedi. Lütfen tekrar deneyin.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitError('');
    onClose();
  };

  if (!user) return null;

  const needsCurrentPassword = userRole === UserRole.HotelUser && user.id === currentUser?.id;
  const isManagerChangingOtherPassword = userRole === UserRole.HotelManager && user.id !== currentUser?.id;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        backgroundColor: '#fff', borderBottom: '1px solid #f0f0f0',
        py: 2, px: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px',
            backgroundColor: 'rgba(245, 124, 0, 0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <LockRoundedIcon sx={{ fontSize: 18, color: '#f57c00' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e' }}>
              Şifre Değiştir
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#999' }}>
              {user.username}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small"
          sx={{ width: 32, height: 32, color: '#999', '&:hover': { color: '#666', backgroundColor: 'rgba(0,0,0,0.04)' } }}>
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {submitError && (
          <Alert severity="error" sx={{
            mb: 2, borderRadius: '10px',
            backgroundColor: 'rgba(229, 57, 53, 0.05)',
            border: '1px solid rgba(229, 57, 53, 0.15)',
          }}>
            {submitError}
          </Alert>
        )}

        {isManagerChangingOtherPassword && (
          <Alert severity="info" sx={{
            mb: 2, borderRadius: '10px',
            backgroundColor: 'rgba(66, 148, 227, 0.05)',
            border: '1px solid rgba(66, 148, 227, 0.15)',
          }}>
            Yönetici olarak mevcut şifreyi bilmeden değiştirebilirsiniz.
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {needsCurrentPassword && (
              <Grid item xs={12}>
                <TextField
                  required fullWidth size="small" name="currentPassword"
                  label="Mevcut Şifre" type="password"
                  value={formData.currentPassword} onChange={handleChange}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                required fullWidth size="small" name="newPassword"
                label="Yeni Şifre" type="password"
                value={formData.newPassword} onChange={handleChange}
                helperText="En az 6 karakter"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required fullWidth size="small" name="confirmPassword"
                label="Yeni Şifre (Tekrar)" type="password"
                value={confirmPassword} onChange={handleChange}
                error={confirmPassword !== '' && formData.newPassword !== confirmPassword}
                helperText={
                  confirmPassword !== '' && formData.newPassword !== confirmPassword
                    ? 'Şifreler eşleşmiyor'
                    : 'Yeni şifreyi tekrar girin'
                }
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
            <Button onClick={handleClose} disabled={submitting}
              sx={{
                textTransform: 'none', fontWeight: 500, fontSize: '0.85rem',
                borderRadius: '10px', px: 2.5, color: '#666',
                backgroundColor: '#f5f5f5', '&:hover': { backgroundColor: '#eee' }
              }}>
              Vazgeç
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                submitting ||
                formData.newPassword !== confirmPassword ||
                formData.newPassword.length < 6 ||
                (needsCurrentPassword && !formData.currentPassword)
              }
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: '0.85rem',
                borderRadius: '10px', px: 2.5,
                backgroundColor: '#f57c00', color: '#fff',
                '&:hover': { backgroundColor: '#ef6c00' },
                '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#999' }
              }}>
              {submitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Şifreyi Değiştir'}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordModal;
